import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { reduxForm, Field, change } from 'redux-form';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { parse, isValid } from 'date-fns';

import { takeQueueItem, getStorageSetting, hideAddFormModal } from 'actions';

import AccessControl from 'components/AccessControl';
import FormTypeSelect from 'components/Common/FormTypeSelect';
import ActivityPerms from 'components/AccessControl/permissions.json';
import {
  TextInput,
  SelectInput,
  preventInvalidDateInitialization,
  RenderDatePicker
} from 'components/Common/Inputs';
import RadioTableGroup from 'components/Common/Inputs/RadioTableGroup';
import CustomerAccountInput from 'components/Common/Inputs/CustomerAccountInput';
import FileUpload from 'components/Common/FileUpload';
import LoadingWrapper from 'components/Common/LoadingWrapper';
import EvidentiaryDocs from 'components/EvidentiaryDocs';
import BeneficialOwnerFormsUpload from 'components/Common/BeneficialOwnerFormsUpload';
import WithholdingStatementUpload from 'components/Common/WithholdingStatementUpload';
import LinkWhsOperationMultiMonitor from 'components/LinkWhsOperationMultiMonitor';

import { ApiService } from 'services';

const isRequired = value => !value || !value.trim();
const isEmailAddress = value => (value ? value.indexOf('@') === -1 : false);

class AddFormsPopupForm extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    imyTier: PropTypes.shape(),
    addImyLayer: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    onSetModalSize: PropTypes.func.isRequired,
    setDisableConfirm: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    isSubmitting: PropTypes.bool,
    results: PropTypes.shape({
      accountIds: PropTypes.arrayOf(PropTypes.number),
      customerIds: PropTypes.arrayOf(PropTypes.shape()),
      success: PropTypes.bool,
      error: PropTypes.shape({
        code: PropTypes.number,
        message: PropTypes.string,
      }),
      files: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    formTypes: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }))
  };

  static defaultProps = {
    results: null,
    permissions: null,
    imyTier: null,
    addImyLayer: null,
    isSubmitting: false,
    formTypes: []
  };

  static onFileUploadCancel(file) {
    toastr.info('', `Upload of ${ file.name } has been canceled`);
  }

  static onFileUploadError(file, error) {
    toastr.error('', `Upload of ${ file.name } has been canceled.  ${ error.message }`);
  }

  static getDuplicateValues = (arrayItems, keyName) => {
    const arraySummary = arrayItems.reduce((prev, curr) => { prev[curr[keyName] || curr] = (prev[curr[keyName] || curr] || 0) + 1; return prev; }, []);
    return Object.keys(arraySummary).filter(x => arraySummary[x] > 1);
  };

  constructor(props) {
    super(props);

    const clients = this.filterClientList() || [];
    const files = getStorageSetting('files') || [];

    const clientKey = (clients != null && clients.length === 1) ? clients[0].key : '';
    const canUploadStmtBos = AccessControl.hasPermission(props.permissions, [ActivityPerms.scannedUpload, ActivityPerms.scannedWhdStmtBoUpload], true);
    const showBeneficiaryTab = props.addImyLayer != null && !props.addImyLayer && canUploadStmtBos;

    this.state = {
      files,
      addImyLayer: props.addImyLayer,
      clientId: clientKey,
      wFormReference: null,
      crsFormReference: null,
      fatcaCrsFormReference: null,
      customerType: null,
      usNonUs: null,
      wFormDate: null,
      crsCustomer: null,
      crsFormDate: null,
      fatcaCrsFormDate: null,
      activeTab: showBeneficiaryTab ? 2 : 0,
      tabs: [
        {
          title: props.imyTier ? 'IMY' : 'W Forms',
          index: 0,
          enabled: props.addImyLayer != null,
          visible: props.imyTier && props.addImyLayer != null && props.addImyLayer
        },
        { title: 'CRS Forms', index: 1, enabled: false, visible: false },
        { title: 'Combined Forms', index: 2, enabled: false, visible: false },
        {
          title: props.imyTier ? 'Beneficiary Forms' : 'IMY',
          index: 3,
          enabled: props.imyTier && showBeneficiaryTab,
          visible: showBeneficiaryTab
        },
        { title: 'Supporting Docs', index: 4, enabled: false, visible: false },
        { title: 'Results', index: 5, enabled: false, visible: false },
      ],
      formType: null,
      crsFormType: null,
      fatcaCrsFormType: null,
      whdStatementType: null,
      whdStatement: null,
      supportingDocType: null,
      showBeneficiaryFileUpload: false,
      originEmail: null,
      clients,
      wForms: [],
      crsForms: [],
      fatcaCrsForms: [],
      benefForms: [],
      supportingDocs: [],
      formErrors: [],
      showAllForms: (props.imyTier == null),
      customerObject: null,
      imyParentCustomer: null,
      imyAccountGuid: null,
      isImyParentCreateNewCustomerChecked: false,
      existingStatements: null,
      initialStatement: null,
      whsLinkAccounts: null,
      showImyOverwriteWarning: false,
      showImyOverwriteWarningCombined: false
    };

    this.onFormTypeChange = this.onFormTypeChange.bind(this);

    props.dispatch(change('uploadingPdfForm', 'addImyLayer', props.addImyLayer));
    if (clientKey != null) {
      props.dispatch(change('uploadingPdfForm', 'clientId', `${ clientKey }`));
    }

    if (props.imyTier) {
      if (props.onSetModalSize) {
        props.onSetModalSize('');
      }
    }
    props.setDisableConfirm(true);
  }

  componentDidUpdate(prevProps) {
    const { imyTier, results, setDisableConfirm } = this.props;
    const { whdStatement } = this.state;

    if (prevProps.results !== results) {
      if (results != null && results.error != null) {
        this.clearStateForSessionResend();
        setDisableConfirm(true);
      }
      if (results !== null && results.success === true) {
        this.clearStateForSessionResend();
        setDisableConfirm(true);
      }
    }

    if (imyTier) {
      const prevParentAccountId = prevProps.imyTier.accountId;
      if (imyTier?.accountId > 0 && (!whdStatement || !prevProps.imyTier || prevParentAccountId !== imyTier.accountId)) {
        this.getWhdStatementOwners(imyTier.accountId);
      }
    }
  }

  onSelectTab(tabIndex) {
    const { isSubmitting } = this.props;
    const { tabs } = this.state;

    if (isSubmitting) {
      return;
    }

    if (tabs != null) {
      Object.keys(tabs).forEach((index) => {
        if (index != null) {
          tabs[index].visible = (tabs[index].index === tabIndex);
        }
      });
    }

    this.setState({
      activeTab: tabIndex,
      tabs
    });
  }

  /* File Uploads */

  onWFormFileUploadSuccess(file, response) {
    const { wForms } = this.state;
    const { setDisableConfirm } = this.props;
    const joined = wForms.concat(response);
    this.setState({ wForms: joined });
    setDisableConfirm(false);

    toastr.success('', `${ response[0].fileName } has been uploaded`);
  }

  onCrsFormFileUploadSuccess(file, response) {
    const { setDisableConfirm } = this.props;
    const { crsForms } = this.state;
    const joined = crsForms.concat(response);
    this.setState({ crsForms: joined });
    setDisableConfirm(false);

    toastr.success('', `${ response[0].fileName } has been uploaded`);
  }

  onFatcaCrsFormFileUploadSuccess(file, response) {
    const { setDisableConfirm } = this.props;
    const { fatcaCrsForms } = this.state;
    const joined = fatcaCrsForms.concat(response);
    this.setState({ fatcaCrsForms: joined });
    setDisableConfirm(false);

    toastr.success('', `${ response[0].fileName } has been uploaded`);
  }

  onBenefFormFileUploadSuccess(file, response) {
    const { setDisableConfirm, imyTier } = this.props;
    const { benefForms } = this.state;
    const joined = benefForms.concat(response);
    this.setState({ benefForms: joined });
    if (imyTier != null) {
      setDisableConfirm(false);
    }

    toastr.success('', `${ response[0].fileName } has been uploaded`);
  }

  onWhdStatementFileUploadError(file, response) {
    this.setState({ whdStatement: null });
  }

  onWhdStatementFileUploadSuccess(file, response) {
    this.setState({ whdStatement: response });
  }

  onSupportingDocFileUploadSuccess(file, response) {
    const { supportingDocs } = this.state;
    const joined = supportingDocs.concat(response);
    this.setState({ supportingDocs: joined });
    toastr.success('', `${ response[0].fileName } has been uploaded`);
  }

  onDeleteWForm(file) {
    const { crsForms } = this.state;
    const { fatcaCrsForms } = this.state;
    let { wForms } = this.state;
    const matched = wForms.filter(f => f.fileName === file.name);
    const { setDisableConfirm } = this.props;

    if (matched != null && matched.length > 0) {
      const fileId = matched[0].id;
      ApiService
        .deleteTempUpload(matched[0].id)
        .then((result) => {
          if (result != null) {
            wForms = wForms.filter(doc => doc.id !== fileId);

            this.setState({ wForms });
            setDisableConfirm((!wForms || wForms.length === 0) && (!crsForms || crsForms.length === 0) && (!fatcaCrsForms || fatcaCrsForms.length === 0));
          } else {
            toastr.error('', `Failed to remove ${ file.name }.  Please try again`);
          }
        })
        .catch(err => toastr.error('', `Failed to remove ${ file.name }.  Please try again`));
    }
  }

  onDeleteCrsForm(file) {
    const { wForms } = this.state;
    const { fatcaCrsForms } = this.state;
    let { crsForms } = this.state;
    const matched = crsForms.filter(f => f.fileName === file.name);
    const { setDisableConfirm } = this.props;

    if (matched != null && matched.length > 0) {
      const fileId = matched[0].id;
      ApiService
        .deleteTempUpload(matched[0].id)
        .then((result) => {
          if (result != null) {
            crsForms = crsForms.filter(doc => doc.id !== fileId);

            this.setState({ crsForms });
            setDisableConfirm((!crsForms || crsForms.length === 0) && (!wForms || wForms.length === 0) && (!fatcaCrsForms || fatcaCrsForms.length === 0));
          } else {
            toastr.error('', `Failed to remove ${ file.name }.  Please try again`);
          }
        })
        .catch(err => toastr.error('', `Failed to remove ${ file.name }.  Please try again`));
    }
  }

  onDeleteFatcaCrsForm(file) {
    const { wForms } = this.state;
    const { crsForms } = this.state;
    let { fatcaCrsForms } = this.state;
    const matched = fatcaCrsForms.filter(f => f.fileName === file.name);
    const { setDisableConfirm } = this.props;

    if (matched != null && matched.length > 0) {
      const fileId = matched[0].id;
      ApiService
        .deleteTempUpload(matched[0].id)
        .then((result) => {
          if (result != null) {
            fatcaCrsForms = fatcaCrsForms.filter(doc => doc.id !== fileId);

            this.setState({ fatcaCrsForms });
            setDisableConfirm((!fatcaCrsForms || fatcaCrsForms.length === 0) && (!crsForms || crsForms.length === 0) && (!wForms || wForms.length === 0));
          } else {
            toastr.error('', `Failed to remove ${ file.name }.  Please try again`);
          }
        })
        .catch(err => toastr.error('', `Failed to remove ${ file.name }.  Please try again`));
    }
  }

  onDeleteBeneficiaryFormSuccess(file, response) {
    const { imyTier, setDisableConfirm } = this.props;
    const { addImyLayer } = this.state;

    if (response != null) {
      let { benefForms } = this.state;
      benefForms = benefForms.filter(doc => doc.id !== file.id);
      this.setState({ benefForms });
      if (imyTier && !addImyLayer) {
        setDisableConfirm(!benefForms || benefForms.length === 0);
      }
    } else {
      toastr.error('', `Failed to remove ${ (file && file.name) ?? 'the file' }.  Please try again`);
    }
  }

  onDeleteWhdStatement(file) {
    const { dispatch } = this.props;
    const { benefForms } = this.state;

    if (file != null) {
      this.setState({ whdStatement: null, benefForms: [] });
      dispatch(change('uploadingPdfForm', 'benefFormType', null));
      if (benefForms && benefForms.length > 0) {
        benefForms.forEach(beneForm => beneForm && beneForm.id && this.onDeleteSupportingDocument(beneForm));
      }
    } else {
      toastr.error('', 'Failed to remove withholding statement.  Please try again');
    }
  }

  onDeleteSupportingDocument(file) {
    ApiService
      .deleteTempUpload(file.id)
      .then((result) => {
        if (result != null) {
          let { supportingDocs } = this.state;
          supportingDocs = supportingDocs.filter(doc => doc.id !== file.id);
          this.setState({ supportingDocs });
        } else {
          toastr.error('', `Failed to remove ${ file.name }.  Please try again`);
        }
      })
      .catch(err => toastr.error('', `Failed to remove ${ file.name }.  Please try again`));
  }

  /* Input Changes */

  onFormTypeChange(e, newValue) {
    const { permissions } = this.props;
    const { customerObject, tabs } = this.state;

    const accountsWhereImyTopLevel = customerObject?.accounts.filter(x => !(x?.parentAccountId) && x?.withholdingStatementId) ?? [];
    tabs[3].enabled = (newValue === 'w8imy' || newValue === 'w8imy_2017' || newValue === 'w8imy_2021') && AccessControl.hasPermission(permissions, [ActivityPerms.scannedUpload, ActivityPerms.scannedWhdStmtBoUpload], true);

    if (tabs[3].enabled) {
      const accountList = customerObject && customerObject.accounts && customerObject.accounts.map(x => x.value);
      this.setState({ showImyOverwriteWarning: false });
      if (accountList && accountList.length > 0) {
        ApiService
          .getAccountWithholdingStatements(accountList)
          .then(result => this.setState({ existingStatements: (result && result.data) || null }))
          .catch(() => toastr.error('', 'Unable to check for existing withholding statements'));
      }
    } else if (accountsWhereImyTopLevel.length > 0 && newValue && (newValue.startsWith('w8') || newValue.startsWith('w9') || newValue.startsWith('fatca'))) {
      this.setState({ showImyOverwriteWarning: true });
    } else {
      this.setState({ showImyOverwriteWarning: false });
    }

    this.setState({
      formType: newValue,
      tabs
    });
  }

  onActiveWhsLinkMonitorsChange(accounts) {
    const { whsLinkAccounts } = this.state;

    if (!accounts) {
      return;
    }
    const newAccounts = whsLinkAccounts.filter(x => accounts.includes(x.value));
    this.setState({ whsLinkAccounts: newAccounts });
  }

  /* Results Actions */

  onTakeForm(id, accountId) {
    const { dispatch } = this.props;

    if (!id) {
      toastr.error('Error', 'Cannot navigate to the form at this time.  Please return to \'Home\' and pick this up from the \'Initial Review\' queue');
      return;
    }

    dispatch(takeQueueItem('form', id, accountId));
  }

  /* Form Validation */

  onFormSubmit(event) {
    const {
      onSubmit,
      imyTier,
      setDisableConfirm
    } = this.props;
    const { tabs, benefForms } = this.state;
    const errors = this.validate();

    tabs[5].enabled = true;
    this.setState({ tabs });

    this.onSelectTab(5);

    if (errors != null && errors.length === 0) {
      onSubmit(imyTier, this.state);

      if (benefForms.length <= 0) {
        setDisableConfirm(true);
      }
      tabs[4].enabled = false;
    } else {
      event.preventDefault();
    }
  }

  getWhdStatementOwners(accountId) {
    const { dispatch } = this.props;

    ApiService
      .getFormStatement(accountId)
      .then((response) => {
        if (!response.data) {
          this.setState({ whdStatement: response.data });
          return;
        }

        const whdStatement = response.data;
        const { imyOwners } = whdStatement;
        const initialStatement = JSON.parse(JSON.stringify(whdStatement));

        this.setState({ imyOwners, whdStatement, initialStatement });

        if (!imyOwners || imyOwners.length === 0) {
          const { tabs } = this.state;
          tabs[0].enabled = true;
          tabs[0].visible = false;
          tabs[3].visible = true;
          tabs[3].enabled = true;
          this.setState({ tabs, activeTab: 2, addImyLayer: false });
        }
      })
      .catch((err) => {
        toastr.error('', `Failed to get WHD Allocation Statement\n${ err }`);
        dispatch(hideAddFormModal());
      });
  }

  getWhdStatementLayerOwners(accountId, accountName) {
    const { dispatch } = this.props;

    ApiService
      .getFormStatementLayerOwners(accountId, accountName)
      .then((response) => {
        const { whdStatement } = this.state;
        if (response.data) {
          const newStatement = JSON.parse(JSON.stringify(whdStatement));
          newStatement.owners = response.data;
          this.setState({ whdStatement: newStatement });
          return;
        }

        this.setState({ whdStatement });
      })
      .catch((err) => {
        toastr.error('', `Failed to get WHD Allocation Statement Tier Owners\n${ err }`);
        dispatch(hideAddFormModal());
      });
  }

  onFormReferenceChange = (e) => {
    this.setState({ wFormReference: e.target.value });
  };

  onCrsFormReferenceChange = (e) => {
    this.setState({ crsFormReference: e.target.value });
  };

  onFatcaCrsFormReferenceChange = (e) => {
    this.setState({ fatcaCrsFormReference: e.target.value });
  };

  onCustomerType = (e, newValue, prevValue) => {
    const { dispatch } = this.props;
    const { wForms, crsForms, fatcaCrsForms, formType, crsFormType, fatcaCrsFormType } = this.state;
    const newFormType = wForms && wForms.length > 0 ? formType : '';
    const newCrsFormType = crsForms && crsForms.length > 0 ? crsFormType : '';
    const newfatcaCrsFormType = fatcaCrsForms && fatcaCrsForms.length > 0 ? fatcaCrsFormType : '';
    dispatch(change('uploadingPdfForm', 'formType', null));
    dispatch(change('uploadingPdfForm', 'crsFormType', null));
    dispatch(change('uploadingPdfForm', 'fatcaCrsFormType', null));
    this.setState({ customerType: newValue, formType: newFormType, crsFormType: newCrsFormType, showAllForms: false, fatcaCrsFormType: newfatcaCrsFormType });
  };

  onUsNonUsChange = (e, newValue, prevValue) => {
    const { dispatch } = this.props;
    const { wForms, crsForms, fatcaCrsForms, formType, crsFormType, fatcaCrsFormType } = this.state;
    const newFormType = wForms && wForms.length > 0 ? formType : '';
    const newCrsFormType = crsForms && crsForms.length > 0 ? crsFormType : '';
    const newfatcaCrsFormType = fatcaCrsForms && fatcaCrsForms.length > 0 ? fatcaCrsFormType : '';
    dispatch(change('uploadingPdfForm', 'formType', null));
    dispatch(change('uploadingPdfForm', 'crsFormType', null));
    dispatch(change('uploadingPdfForm', 'fatcaCrsFormType', null));
    this.setState({ usNonUs: newValue, formType: newFormType, crsFormType: newCrsFormType, showAllForms: false, fatcaCrsFormType: newfatcaCrsFormType });
  };

  onImyAccountSelectedItem = (lookupValue) => {
    const { dispatch, imyTier } = this.props;
    this.setState({ imyAccountGuid: lookupValue });
    if (imyTier) {
      this.getWhdStatementLayerOwners(imyTier.accountId, lookupValue || null);
    }
    dispatch(change('uploadingPdfForm', 'addImyOwner', lookupValue));
  };

  onImyAccountChange = (e, newValue) => {
    const { dispatch, imyTier } = this.props;
    this.setState({ imyAccountGuid: newValue });
    if (imyTier) {
      this.getWhdStatementLayerOwners(imyTier.accountId, newValue || null);
    }
    dispatch(change('uploadingPdfForm', 'addImyOwner', newValue));
  };

  onWFormDateChange = (e, newValue, prevValue) => this.setState({ wFormDate: newValue });

  onCrsFormTypeChange = (e, newValue) => this.setState({ crsFormType: newValue });

  onFatcaCrsFormTypeChange = (e, newValue) => {
    const { customerObject } = this.state;
    const accountsWhereImyTopLevel = customerObject?.accounts.filter(x => !(x?.parentAccountId) && x?.withholdingStatementId) ?? [];

    if (accountsWhereImyTopLevel.length > 0 && newValue && (newValue.startsWith('fatca'))) {
      this.setState({ showImyOverwriteWarningCombined: true });
    } else {
      this.setState({ showImyOverwriteWarningCombined: false });
    }

    this.setState({ fatcaCrsFormType: newValue });
  };

  onCrsFormDateChange = (e, newValue, prevValue) => this.setState({ crsFormDate: newValue });

  onFatcaCrsFormDateChange = (e, newValue, prevValue) => this.setState({ fatcaCrsFormDate: newValue });

  onWhdStatementTypeChange = (e, newValue) => this.setState({ whdStatementType: newValue, showBeneficiaryFileUpload: (newValue !== 'qipooled' && newValue !== 'nqipooled' && newValue !== 'pooledwhdstatement') });

  onOriginEmailChange = (e, newValue) => this.setState({ originEmail: newValue.trim() });

  onSupportingDocTypeChange = (e, newValue) => this.setState({ supportingDocType: newValue });

  onImyBeneficiarySelected = (e, value, index) => {
    const { benefForms, whdStatement } = this.state;
    const owner = whdStatement && whdStatement.owners && whdStatement.owners.find(o => o.guid === value);

    benefForms[index].subAccountId = owner && owner.accountId;
    benefForms[index].subAccountName = owner && owner.name;
    benefForms[index].subAccountGuid = owner && owner.guid;
    this.setState({ benefForms });
  };

  onImyChoiceChange = (newValue) => {
    const { tabs, addImyLayer, initialStatement } = this.state;
    const { dispatch } = this.props;

    if (newValue === addImyLayer) {
      return;
    }

    tabs[0].enabled = true;
    tabs[3].enabled = !newValue;

    this.setState({ imyAccountGuid: null, wForms: [], whdStatement: initialStatement });
    dispatch(change('uploadingPdfForm', 'addImyOwner', null));
    dispatch(change('uploadingPdfForm', 'formType', null));
    dispatch(change('uploadingPdfForm', 'benefFormType', null));

    dispatch(change('uploadingPdfForm', 'addImyLayer', newValue));
    this.setState({ tabs, wForms: [], benefForms: [], formType: null, addImyLayer: newValue }, () => {
      this.onSelectTab(newValue ? 0 : 2);
    });
  };

  setCustomerObjectCallback(data) {
    const { permissions, onSetModalSize } = this.props;
    this.setState({ customerObject: data });

    if (data.accounts && data.accounts.length > 0) {
      this.setState({ whsLinkAccounts: data.accounts });
    } else {
      this.setState({ whsLinkAccounts: [] });
    }

    const { tabs } = this.state;
    this.clearStateForSessionResend();

    const crsEnabled = data.legalEntities != null
      && data.legalEntities.filter(le => (le.chapter4Status === 6 || le.chapter4Status === 7) || le.crsParticipating).length > 0;

    const combinedAllowed = data.legalEntities != null
      && data.legalEntities.filter(le => le.chapter4Status === 1 || le.chapter4Status === 2).length === 0;

    tabs[0].enabled = data.selectionValid;
    tabs[1].enabled = data.selectionValid && crsEnabled;
    tabs[2].enabled = data.selectionValid && combinedAllowed && this.fatcaCrsFormsLicensed();
    tabs[4].enabled = data.selectionValid && AccessControl.hasPermission(permissions, [ActivityPerms.scannedSupDocs, ActivityPerms.scannedSupDocsInternal]);
    tabs[5].enabled = false;

    if (tabs.filter(t => t.enabled).length && onSetModalSize) {
      onSetModalSize('');
    } else {
      onSetModalSize('modal-allow-overflow');
    }

    this.setState({
      tabs
    });

    this.onSelectTab(0);
  }

  // eslint-disable-next-line react/sort-comp, class-methods-use-this
  filterClientList = () => {
    const clients = getStorageSetting('clients') || [];
    const selectedClient = getStorageSetting('selectedClient');
    let filteredClientList = clients;

    if (selectedClient != null) {
      filteredClientList = [];
      filteredClientList.push(selectedClient);
    }

    return filteredClientList;
  };

  fatcaCrsFormsLicensed() {
    const { formTypes } = this.props;
    if (formTypes && formTypes.length > 1) {
      for (let i = 0; i < formTypes.length; i += 1) {
        if (formTypes[i].key === 'fatca_crse_2022' || formTypes[i].key === 'fatca_crsi_2022' || formTypes[i].key === 'fatca_crsi_2023' || formTypes[i].key === 'fatca_crse_2023') {
          return true;
        }
      }
    }
    return false;
  }

  fieldHasErrors(field) {
    const { formErrors } = this.state;

    return formErrors != null && formErrors.filter(error => error.field === field).length > 0;
  }

  validate() {
    const { imyTier } = this.props;
    const { clientId,
      formType, wForms,
      crsFormType, crsForms,
      fatcaCrsFormType, fatcaCrsForms,
      wFormDate, crsFormDate, fatcaCrsFormDate,
      whdStatementType, whdStatement,
      imyAccountGuid,
      originEmail, benefForms, addImyLayer,
      wFormReference, crsFormReference, fatcaCrsFormReference } = this.state;
    let { formErrors } = this.state;

    formErrors = [];
    if (!imyTier) {
      if (!clientId) {
        formErrors.push({ field: 'client', message: 'A Client is required' });
      }
    }

    if (imyTier && !addImyLayer && (!benefForms || benefForms.length === 0) && (!formType || !wForms || !wForms.length === 0)) {
      formErrors.push({ field: 'benefForms', message: 'At least one beneficiary form or a replacment W-8IMY form should be uploaded' });
    } else if (!imyTier || (imyTier && addImyLayer)) {
      if (!(wForms && wForms.length > 0)
        && !(crsForms && crsForms.length > 0)
        && !(fatcaCrsForms && fatcaCrsForms.length > 0)) {
        formErrors.push({ field: 'wFormCrsForm', message: 'At least one form should be uploaded' });
      } else {
        if (formType || (wForms && wForms.length > 0)) {
          if (!formType) {
            formErrors.push({ field: 'formType', message: 'A W Form Type is required' });
          }

          if (!wForms == null || wForms.length === 0) {
            formErrors.push({ field: 'wForm', message: 'A W Form must be uploaded' });
          }
        }

        if (!imyTier && (crsFormType || (crsForms && crsForms.length > 0))) {
          if (!crsFormType) {
            formErrors.push({ field: 'crsFormType', message: 'A CRS Form Type is required' });
          }

          if (!crsForms == null || crsForms.length === 0) {
            formErrors.push({ field: 'crsForms', message: 'A CRS Form must be uploaded' });
          }
        }

        if (!imyTier && (fatcaCrsFormType || (fatcaCrsForms && fatcaCrsForms.length > 0))) {
          if (!fatcaCrsFormType) {
            formErrors.push({ field: 'fatcaCrsFormType', message: 'A Combined Form Type is required' });
          }

          if (!fatcaCrsForms == null || fatcaCrsForms.length === 0) {
            formErrors.push({ field: 'fatcaCrsForms', message: 'A Combined Form must be uploaded' });
          }
        }

        if (imyTier) {
          if (imyAccountGuid == null && addImyLayer) {
            formErrors.push({ field: 'addImyOwner', message: 'Please select the appropriate Beneficial Owner account for the W-8IMY form.' });
          }
        }
      }

      if (formType != null && formType.indexOf('imy') !== -1) {
        if (!imyTier && whdStatementType && !whdStatement) {
          formErrors.push({ field: 'whdStatement', message: 'An Withholding statement must be uploaded' });
        } else if (!imyTier && !whdStatementType && whdStatement) {
          formErrors.push({ field: 'whdStatementType', message: 'A Withholding statement type is required' });
        }
      }

      if (wFormDate) {
        const parsedDate = parse(wFormDate, 'MMM dd, yyyy', new Date());
        if (!isValid(parsedDate)) {
          formErrors.push({ field: 'wFormDate', message: 'Invalid Signature Date on W Forms Tab' });
        }
      }

      if (crsFormDate) {
        const parsedDate = parse(crsFormDate, 'MMM dd, yyyy', new Date());
        if (!isValid(parsedDate)) {
          formErrors.push({ field: 'crsFormDate', message: 'Invalid Signature Date on CRS Forms Tab' });
        }
      }

      if (fatcaCrsFormDate) {
        const parsedDate = parse(fatcaCrsFormDate, 'MMM dd, yyyy', new Date());
        if (!isValid(parsedDate)) {
          formErrors.push({ field: 'fatcaCrsFormDate', message: 'Invalid Signature Date on Combined Forms Tab' });
        }
      }

      if (originEmail && originEmail.indexOf('@') === -1) {
        formErrors.push({ field: 'originEmail', message: 'The origin email entered is not a valid email address' });
      }

      if (wFormReference != null && wFormReference.length > 150) {
        formErrors.push({ field: 'form-reference', message: 'The w-form reference supplied is longer than 150 characters' });
      }

      if (wFormReference != null && wFormReference.match(/[^A-Za-z\d_/.\\-]/)) {
        formErrors.push({ field: 'form-reference', message: 'No spaces or special characters allowed in the w-form reference' });
      }

      if (crsFormReference != null && crsFormReference.length > 150) {
        formErrors.push({ field: 'crs-form-reference', message: 'The crs-form reference supplied is longer than 150 characters' });
      }

      if (crsFormReference != null && crsFormReference.match(/[^A-Za-z\d_/.\\-]/)) {
        formErrors.push({ field: 'crs-form-reference', message: 'No spaces or special characters allowed in the crs-form reference' });
      }

      if (fatcaCrsFormReference != null && fatcaCrsFormReference.length > 150) {
        formErrors.push({ field: 'combined-form-reference', message: 'The combined-form reference supplied is longer than 150 characters' });
      }

      if (fatcaCrsFormReference != null && fatcaCrsFormReference.match(/[^A-Za-z\d_/.\\-]/)) {
        formErrors.push({ field: 'combined-form-reference', message: 'No spaces or special characters allowed in the combined-form reference' });
      }
    }

    if (whdStatement && benefForms && benefForms.length > 0) {
      let missingBeneficiaries = false;
      let duplicatedBeneficiaries = false;
      const duplicateBenef = AddFormsPopupForm.getDuplicateValues(benefForms, 'subAccountName');

      benefForms.forEach((value, idx) => {
        if (!value.subAccountName && !value.subAccountId) {
          formErrors.push({ field: `imyBeneficiaryAccount-${ idx }`, message: !missingBeneficiaries && 'One or more IMY beneficiary has not been selected' });
          missingBeneficiaries = true;
        } else if (duplicateBenef.length > 0 && duplicateBenef.indexOf(value.subAccountName) > -1) {
          formErrors.push({ field: `imyBeneficiaryAccount-${ idx }`, message: !duplicatedBeneficiaries && 'IMY Beneficiary has been added twice' });
          duplicatedBeneficiaries = true;
        }
      });
    }

    this.setState({
      formErrors
    });

    return formErrors;
  }

  clearStateForSessionResend() {
    const { dispatch } = this.props;
    dispatch(change('uploadingPdfForm', 'formType', null));
    dispatch(change('uploadingPdfForm', 'crsFormType', null));
    dispatch(change('uploadingPdfForm', 'fatcaCrsFormType', null));
    dispatch(change('uploadingPdfForm', 'sourceEmailAddress', null));
    dispatch(change('uploadingPdfForm', 'supportingDocType', null));
    this.setState({
      wFormReference: null,
      formType: null,
      wForms: [],
      crsFormReference: null,
      crsFormType: null,
      crsForms: [],
      fatcaCrsFormReference: null,
      fatcaCrsFormType: null,
      fatcaCrsForms: [],
      originEmail: null,
      supportingDocType: null,
      supportingDocs: [],
      formErrors: [],
      existingStatements: null,
      showImyOverwriteWarning: false,
      showImyOverwriteWarningCombined: false
    });

    this.onFormTypeChange(null, null);
  }

  render() {
    const {
      id,
      imyTier,
      isSubmitting,
      results,
      setDisableConfirm,
      permissions
    } = this.props;

    const {
      files,
      formErrors,
      addImyLayer,
      clientId,
      customerType,
      usNonUs,
      crsFormType,
      fatcaCrsFormType,
      formType,
      whdStatement,
      activeTab,
      tabs,
      date,
      showBeneficiaryFileUpload,
      benefForms,
      supportingDocType,
      supportingDocs,
      showAllForms,
      imyAccountGuid,
      existingStatements,
      imyOwners,
      whsLinkAccounts,
      customerObject,
      showImyOverwriteWarning,
      showImyOverwriteWarningCombined
    } = this.state;

    const individual = customerType === 'individual';
    const entity = customerType === 'entity';
    const usPerson = usNonUs === 'us';
    const usNonUsGiven = usNonUs != null;
    const resultAccount = results && results.accountIds && results.accountIds.length === 1 && results.accountIds[0];

    const accountsPartOfAnImy = customerObject?.accounts.filter(x => x?.parentAccountId) ?? [];
    const accountsWhereImyTopLevel = customerObject?.accounts.filter(x => !(x?.parentAccountId) && x?.withholdingStatementId) ?? [];
    const displayAddFormTabs = (imyTier || !whsLinkAccounts || whsLinkAccounts.length === 0) && accountsPartOfAnImy?.length === 0;
    const isTopLevelImy = accountsWhereImyTopLevel.length > 0;

    return (
      <div className="add-form-container">
        <form
          id={ id }
          onSubmit={ values => this.onFormSubmit(values) }
          className="form-horizontal"
          noValidate
        >
          { !imyTier && <div id="add-form-head" className="add-form-head">
            <CustomerAccountInput
              clientId={ clientId || 0 }
              onSelectionChange={ data => this.setCustomerObjectCallback(data) }
              accountsPartOfAnImyStructure={ accountsPartOfAnImy }
            />
            <div className="form-group">
              <div className="col-4 col-sm-12">
                <label htmlFor="customer-type" className="form-label text-right">Customer Type&nbsp;&nbsp;</label>
              </div>
              <div className="col-8 col-sm-12">
                <RadioTableGroup
                  name="customerType"
                  items={ [[{ text: 'Individual', value: 'individual' }, { text: 'Entity', value: 'entity' }]] }
                  preventReset
                  onChange={ (e, newVal, prevVal) => this.onCustomerType(e, newVal, prevVal) }
                  className={ this.fieldHasErrors('customerType') ? 'is-error' : '' }
                />
              </div>
            </div>
            <div className="form-group">
              <div className="col-4 col-sm-12">
                <label htmlFor="us-non-us" className="form-label text-right">Customer US / Non-US Status&nbsp;&nbsp;</label>
              </div>
              <div className="col-8 col-sm-12">
                <RadioTableGroup
                  name="usNonUsPerson"
                  items={ [[{ text: 'US Person', value: 'us' }, { text: 'Non-US Person', value: 'nonUs' }]] }
                  preventReset
                  onChange={ (e, newVal, prevVal) => this.onUsNonUsChange(e, newVal, prevVal) }
                  className={ this.fieldHasErrors('usNonUs') ? 'is-error' : '' }
                />
              </div>
            </div>
          </div> }
          { imyTier && <div id="add-form-head" className="add-form-head">
            <div className="columns">
              <div className="col-6 tier-action">
                <button
                  type="button"
                  className={ addImyLayer === true ? 'active' : '' }
                  disabled={ !imyOwners || imyOwners.length === 0 }
                  onClick={ () => this.onImyChoiceChange(true) }
                >
                  Upload New W-8IMY
                </button>
              </div>
              <div className="col-6 tier-action">
                <button
                  type="button"
                  className={ addImyLayer === false ? 'active' : '' }
                  onClick={ () => this.onImyChoiceChange(false) }
                >
                  Upload Tier Forms
                </button>
              </div>
            </div>
            { addImyLayer != null && imyOwners && imyOwners.length > 0 && <div className="form-group mt-10">
              { !(imyOwners && addImyLayer) && <div className="col-12 py-2 my-2" /> }
              { imyOwners && addImyLayer && <React.Fragment>
                <div className="col-5">
                  <label htmlFor="addImyOwner" className="form-label">Select IMY Form</label>
                </div>
                <div className="col-7">
                  <Field
                    id="add-beneficial-imy-owner"
                    name="addImyOwner"
                    component={ SelectInput }
                    value={ imyAccountGuid }
                    className="form-select"
                    onSelectedItem={ lookupValue => this.onImyAccountSelectedItem(lookupValue) }
                    onChange={ (e, newVal) => this.onImyAccountChange(e, newVal) }
                  >
                    <option value="" disabled>Please select</option>
                    { imyOwners.map(x => <option key={ x.guid } value={ x.guid }>{ x.name }{ x.isDuped && ` (${ x.index })` }</option>) }
                  </Field>
                </div>
              </React.Fragment> }
            </div> }
            { addImyLayer != null && imyOwners && imyOwners.length === 0 && <div className="form-group">
              <div className="col-12 py-2">
                <div className="text-center my-2 py-2">There are no IMY tiers that need to be uploaded for this tree</div>
              </div>
            </div> }
          </div> }
          { displayAddFormTabs
          && <div id="add-form-body" className="add-form-body">
            <div className="add-form-tabs">
              <ul className="tab">
                { tabs != null && tabs.filter(t => t.enabled).map(tab => tab != null && (
                  <li
                    key={ tab.index }
                    className={ `tab-item ${ tab.enabled && tab.visible && tab.index === activeTab ? 'active' : '' }` }
                  >
                    <button
                      type="button"
                      tabIndex={ tab.index }
                      onClick={ () => this.onSelectTab(tab.index) }
                    >{ tab.title }</button>
                  </li>
                )) }
              </ul>
            </div>
            <div className="tab-body">
              { tabs != null && tabs[0] != null && tabs[0].enabled && <div id="tab-w-forms" className={ `${ tabs[0].enabled && tabs[0].visible ? 'active' : '' }` }>
                <div className="form-group">
                  <div className="col-6">
                    { !imyTier && <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="signature-date" className="form-label">Signature Date</label>
                      </div>
                      <div className="full-width-datepicker col-8 col-sm-12">
                        <Field
                          id="signature-date"
                          name="signatureDate"
                          component={ RenderDatePicker }
                          { ... { popperPlacement: 'right-end' } }
                          className={ `form-input ${ this.fieldHasErrors('wFormDate') ? 'is-error' : '' }` }
                          placeholder="MMM DD, YYYY"
                          required
                          onBlur={ preventInvalidDateInitialization }
                          onChange={ (e, newVal, prevVal) => { this.onWFormDateChange(e, newVal); } }
                        />
                      </div>
                    </div> }
                    <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="form-reference" className="form-label">Form Reference</label>
                      </div>
                      <div className="col-8 col-sm-12">
                        <input
                          id="form-reference"
                          name="form-reference"
                          className={ `form-input ${ this.fieldHasErrors('form-reference') ? 'is-error' : '' }` }
                          type="text"
                          placeholder="Enter reference..."
                          onChange={ e => this.onFormReferenceChange(e) }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-6">
                    <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="w-form-type" className="form-label">Form Type *</label>
                      </div>
                      <div className="col-8 col-sm-12">
                        <FormTypeSelect
                          id="pdf-form-type"
                          name="formType"
                          component={ SelectInput }
                          className={ `form-select ${ this.fieldHasErrors('formType') ? 'is-error' : '' }` }
                          required
                          validate={ isRequired }
                          value={ formType }
                          showAllForms={ showAllForms }
                          individual={ individual }
                          entity={ entity }
                          addImyLayer={ addImyLayer != null }
                          usNonUsGiven={ usNonUsGiven }
                          usPerson={ usPerson }
                          onChange={ this.onFormTypeChange }
                          showWFormTypes
                        />
                      </div>
                    </div>
                    <div className={ `form-group ${ formType === null || formType === '' ? 'none' : '' }` }>
                      <div className="col-12 col-sm-12">
                        <FileUpload
                          defaultMessage="Drop files here to upload in pdf format"
                          postUrl={ ApiService.getFileUploadUrl() }
                          accessTokenName={ ApiService.getAccessTokenName() }
                          acceptedFiles="application/pdf"
                          maxFilesize={ files.maxFilesize }
                          timeout={ files.uploadTimeout }
                          docCategory="form"
                          docType={ formType != null ? formType : '' }
                          onFileUploadCancel={ file => AddFormsPopupForm.onFileUploadCancel(file) }
                          onFileUploadSuccess={ (file, response) => this.onWFormFileUploadSuccess(file, response) }
                          onFileUploadError={ (file, error) => AddFormsPopupForm.onFileUploadError(file, error) }
                          onFileRemoved={ file => this.onDeleteWForm(file) }
                          clearFiles={ formType === null }
                        />
                      </div>
                    </div>
                  </div>
                  { isTopLevelImy && showImyOverwriteWarning && <div className="offset-6 offset-sm-none mt-2 col-6 pl-2rem col-sm-12 error-container">
                    <div className="error-text">
                      <p>Uploading a W8/W9 form will impact the Intermediary Structure linked to the selected account{ accountsWhereImyTopLevel.length > 1 ? 's' : '' }</p>
                    </div>
                  </div> }
                </div>
              </div> }
              { tabs != null && tabs[1] != null && tabs[1].enabled && <div id="tab-crs-forms" className={ `${ tabs[1].enabled && tabs[1].visible ? 'active' : '' }` }>
                <div className="form-group">
                  <div className="col-6">
                    <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="crs-signature-date" className="form-label">Signature Date</label>
                      </div>
                      <div className="full-width-datepicker col-8 col-sm-12">
                        <Field
                          id="crs-signature-date"
                          name="crsSignatureDate"
                          component={ RenderDatePicker }
                          { ... { popperPlacement: 'right-end' } }
                          className={ `form-input ${ this.fieldHasErrors('crsFormDate') ? 'is-error' : '' }` }
                          placeholder="MMM DD, YYYY"
                          required={ tabs[1].enabled }
                          onBlur={ preventInvalidDateInitialization }
                          onChange={ (e, newVal, prevVal) => { this.onCrsFormDateChange(e, newVal); } }
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="crs-form-reference" className="form-label">Form Reference</label>
                      </div>
                      <div className="col-8 col-sm-12">
                        <input
                          id="crs-form-reference"
                          name="crs-form-reference"
                          className={ `form-input ${ this.fieldHasErrors('crs-form-reference') ? 'is-error' : '' }` }
                          type="text"
                          placeholder="Enter reference..."
                          onChange={ e => this.onCrsFormReferenceChange(e) }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="crs-form-type" className="form-label">Form Type *</label>
                      </div>
                      <div className="col-8 col-sm-12">
                        <FormTypeSelect
                          id="crs-form-type"
                          name="crsFormType"
                          component={ SelectInput }
                          className={ `form-select ${ this.fieldHasErrors('crsFormType') ? 'is-error' : '' }` }
                          required={ tabs[1].enabled }
                          validate={ isRequired }
                          value={ crsFormType }
                          showAllForms={ showAllForms }
                          individual={ individual }
                          entity={ entity }
                          onChange={ this.onCrsFormTypeChange }
                          showCrsFormTypes
                        />
                      </div>
                    </div>
                    { (crsFormType === 'ucrse_2025' || crsFormType === 'ucrscp_2019' || crsFormType === 'ucrsi_2025') && <div className="form-group">
                      <div className="col-12 col-sm-12">
                        <p><i>Text reading of uploaded Universal CRS forms is not supported. Once submitted, you can refer to the uploaded form to create a digital form for validation.</i></p>
                      </div>
                    </div> }
                    <div className={ `form-group ${ crsFormType === null || crsFormType === '' ? 'none' : '' }` }>
                      <div className="col-12 col-sm-12">
                        <FileUpload
                          defaultMessage="Drop files here to upload in pdf format"
                          postUrl={ ApiService.getFileUploadUrl() }
                          accessTokenName={ ApiService.getAccessTokenName() }
                          acceptedFiles="application/pdf"
                          maxFilesize={ files.maxFilesize }
                          timeout={ files.uploadTimeout }
                          docCategory="form"
                          docType={ crsFormType != null ? crsFormType : '' }
                          onFileUploadCancel={ file => AddFormsPopupForm.onFileUploadCancel(file) }
                          onFileUploadSuccess={ (file, response) => this.onCrsFormFileUploadSuccess(file, response) }
                          onFileUploadError={ (file, error) => AddFormsPopupForm.onFileUploadError(file, error) }
                          onFileRemoved={ file => this.onDeleteCrsForm(file) }
                          clearFiles={ crsFormType === null }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div> }
              { tabs != null && tabs[2] != null && tabs[2].enabled && <div id="tab-combined-forms" className={ `${ tabs[2].enabled && tabs[2].visible ? 'active' : '' }` }>
                <div className="form-group">
                  <div className="col-6">
                    <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="combined-signature-date" className="form-label">Signature Date</label>
                      </div>
                      <div className="full-width-datepicker col-8 col-sm-12">
                        <Field
                          id="combined-signature-date"
                          name="combinedSignatureDate"
                          component={ RenderDatePicker }
                          { ... { popperPlacement: 'right-end' } }
                          className={ `form-input ${ this.fieldHasErrors('fatcaCrsFormDate') ? 'is-error' : '' }` }
                          placeholder="MMM DD, YYYY"
                          required={ tabs[2].enabled }
                          onBlur={ preventInvalidDateInitialization }
                          onChange={ (e, newVal, prevVal) => { this.onFatcaCrsFormDateChange(e, newVal); } }
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="combined-form-reference" className="form-label">Form Reference</label>
                      </div>
                      <div className="col-8 col-sm-12">
                        <input
                          id="combined-form-reference"
                          name="combined-form-reference"
                          className={ `form-input ${ this.fieldHasErrors('combined-form-reference') ? 'is-error' : '' }` }
                          type="text"
                          placeholder="Enter reference..."
                          onChange={ e => this.onFatcaCrsFormReferenceChange(e) }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="combined-form-type" className="form-label">Form Type *</label>
                      </div>
                      <div className="col-8 col-sm-12">
                        <FormTypeSelect
                          id="combined-form-type"
                          name="fatcaCrsFormType"
                          component={ SelectInput }
                          className={ `form-select ${ this.fieldHasErrors('fatcaCrsFormType') ? 'is-error' : '' }` }
                          required={ tabs[2].enabled }
                          validate={ isRequired }
                          value={ fatcaCrsFormType }
                          showAllForms={ showAllForms }
                          individual={ individual }
                          entity={ entity }
                          onChange={ this.onFatcaCrsFormTypeChange }
                          showFatcaCrsFormTypes
                        />
                      </div>
                    </div>
                    <div className={ `form-group ${ fatcaCrsFormType === null || fatcaCrsFormType === '' ? 'none' : '' }` }>
                      <div className="col-12 col-sm-12">
                        <FileUpload
                          defaultMessage="Drop files here to upload in pdf format"
                          postUrl={ ApiService.getFileUploadUrl() }
                          accessTokenName={ ApiService.getAccessTokenName() }
                          acceptedFiles="application/pdf"
                          maxFilesize={ files.maxFilesize }
                          timeout={ files.uploadTimeout }
                          docCategory="form"
                          docType={ fatcaCrsFormType != null ? fatcaCrsFormType : '' }
                          onFileUploadCancel={ file => AddFormsPopupForm.onFileUploadCancel(file) }
                          onFileUploadSuccess={ (file, response) => this.onFatcaCrsFormFileUploadSuccess(file, response) }
                          onFileUploadError={ (file, error) => AddFormsPopupForm.onFileUploadError(file, error) }
                          onFileRemoved={ file => this.onDeleteFatcaCrsForm(file) }
                          clearFiles={ fatcaCrsFormType === null }
                        />
                      </div>
                    </div>
                  </div>
                </div>
                { isTopLevelImy && showImyOverwriteWarningCombined && <div className="offset-6 offset-sm-none mt-2 col-6 pl-2rem col-sm-12 error-container">
                  <div className="error-text">
                    <p>Uploading a Combined form will impact the Intermediary Structure linked to the selected account{ accountsWhereImyTopLevel.length > 1 ? 's' : '' }</p>
                  </div>
                </div> }
              </div>}
              { tabs != null && tabs[3] != null && tabs[3].enabled && <div id="tab-imy" className={ `${ tabs[3].enabled && tabs[3].visible ? 'active' : '' }` }>
                { !imyTier && <WithholdingStatementUpload
                  formErrors={ formErrors }
                  onStatementTypeChange={ (e, newValue) => this.onWhdStatementTypeChange(e, newValue) }
                  onFileUploadSuccess={ (file, response) => this.onWhdStatementFileUploadSuccess(file, response) }
                  onFileUploadError={ (file, error) => this.onWhdStatementFileUploadError(file, error) }
                  onFileUploadCancel={ file => AddFormsPopupForm.onFileUploadCancel(file) }
                  onFileDeleteSuccess={ file => this.onDeleteWhdStatement(file) }
                  disableConfirm={ e => setDisableConfirm(e) }
                  existingStatements={ existingStatements }
                /> }
                { /* Beneficiary Form Upload --> */ }
                { whdStatement && whdStatement.owners && (imyTier || showBeneficiaryFileUpload) && <BeneficialOwnerFormsUpload
                  benefForms={ benefForms ?? [] }
                  formErrors={ formErrors ?? [] }
                  whdStatement={ whdStatement }
                  onBeneficiarySelected={ (e, value, index) => { this.onImyBeneficiarySelected(e, value, index); } }
                  onFileUploadSuccess={ (file, response) => { this.onBenefFormFileUploadSuccess(file, response); } }
                  onFileDeleteSuccess={ (file, response) => { this.onDeleteBeneficiaryFormSuccess(file, response); } }
                /> }
              </div> }
              { tabs != null && tabs[4] != null && tabs[4].enabled && <div id="tab-supporting-docs" className={ `${ tabs[4].enabled && tabs[4].visible ? 'active' : '' }` }>
                <div className="form-group mb">
                  <div className="col-2 col-sm-12">
                    <label htmlFor="source-email-address" className="form-label form-label-padding">Source Email</label>
                  </div>
                  <div style={ { marginLeft: '-2px' } } className="col-10 col-sm-12">
                    <Field
                      id="source-email-address"
                      name="sourceEmailAddress"
                      component={ TextInput }
                      className={ `form-input ${ this.fieldHasErrors('whdStmtType') ? 'is-error' : '' }` }
                      placeholder="Enter source email address"
                      type="email"
                      selected={ date }
                      hideHints
                      normalize={ value => value && value.trim() }
                      validate={ isEmailAddress }
                      onChange={ (e, newValue) => this.onOriginEmailChange(e, newValue) }
                    />
                  </div>
                </div>
                <div className="form-group">
                  <div className="col-6">
                    <div className="form-group">
                      <div className="col-4 col-sm-12">
                        <label htmlFor="supporting-doc-type" className="form-label">Document Type</label>
                      </div>
                      <div className="col-8 col-sm-12">
                        <Field
                          id="supporting-doc-type"
                          name="supportingDocType"
                          component={ EvidentiaryDocs }
                          className="form-select"
                          hideHints
                          value={ supportingDocType }
                          onChange={ (e, newValue) => this.onSupportingDocTypeChange(e, newValue) }
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="col-12 col-sm-12">
                        <FileUpload
                          defaultMessage="Drop files here to upload in bmp, gif, jpg, png, tif, doc, docx, xls, xlsx, msg or pdf format"
                          postUrl={ ApiService.getFileUploadUrl() }
                          accessTokenName={ ApiService.getAccessTokenName() }
                          acceptedFiles="application/pdf,.jpg,.png,.bmp,.msg,.doc,.docx,.jpeg,.tiff,.gif,.tif,.xls,.xlsx"
                          maxFilesize={ files.maxFilesize }
                          timeout={ files.uploadTimeout }
                          docCategory="supportingdocument"
                          docType={ supportingDocType != null ? supportingDocType : '' }
                          removeFileAfterUpload
                          onFileUploadCancel={ file => AddFormsPopupForm.onFileUploadCancel(file) }
                          onFileUploadSuccess={ (file, response) => this.onSupportingDocFileUploadSuccess(file, response) }
                          onFileUploadError={ (file, error) => AddFormsPopupForm.onFileUploadError(file, error) }
                          clearFiles={ supportingDocType === null }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <div className="col-12">
                        <table className="table table-striped table-hover">
                          <thead>
                            <tr><th colSpan="2">Documents Uploaded</th></tr>
                          </thead>
                          <tbody>
                            { supportingDocs != null && supportingDocs.map(doc => doc != null && <tr key={ doc.id }>
                              <td className="col-11 uploaded-filename-td">{ `${ doc.fileName }` }</td>
                              <td className="col-1 text-right">
                                <AccessControl permissions={ permissions } allowedPermissions={ [ActivityPerms.scannedSupDocs] }>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    type="button"
                                    title="Delete document"
                                    onClick={ () => this.onDeleteSupportingDocument(doc) }
                                  >
                                    <span className="icon icon-delete" />
                                  </button>
                                </AccessControl>
                              </td>
                            </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div> }
              { tabs != null && tabs[5] && tabs[5].enabled && <div id="tab-results" className={ `${ tabs[5].enabled && tabs[5].visible ? 'active' : '' }` }>
                <div className="results-container">
                  { !isSubmitting && formErrors === null && results == null && <p>
                    <strong>The form has not been submitted.  Click &apos;Submit&apos; to continue.</strong>
                  </p> }
                  { !isSubmitting && formErrors != null && formErrors.length > 0 && <div className="validation-container">
                    <h2><strong>Validation Errors</strong></h2>
                    <ul>
                      { formErrors != null && formErrors.map(error => error != null && error.message && <li key={ error.field }>{ error.message }</li>) }
                    </ul>
                  </div> }
                  { isSubmitting && <LoadingWrapper loaded={ !isSubmitting } /> }
                  { !isSubmitting && results != null && <div>
                    <h2><strong>Document upload results</strong></h2>
                    <p>
                      <strong className={ results.success ? 'is-success' : 'is-error' }>
                        Overall Result: { results.success ? 'Successful' : 'Failed' }
                      </strong><br /><span className={ results.success ? '' : 'is-error' }>Please review the documents uploaded below.</span>
                    </p>
                    { results.error != null && <p className="is-error"><strong>Error: </strong>{ results.error.message }</p>}
                    { (results.customerIds || (results.files != null && results.files.filter(f => f.customer != null).length > 0)) && <table className="table table-add-form-results table-striped table-hover mb">
                      <thead>
                        <tr>
                          <th className="col-5" colSpan="2">Temporary Customer ID(s) created</th>
                        </tr>
                      </thead>
                      <tbody>
                        { results.customerIds != null && results.customerIds.map(cust => cust != null && <tr key={ cust.key }>
                          <td colSpan="2">{ `${ cust.value }` }</td>
                        </tr>)}
                      </tbody>
                    </table> }
                    { results.files != null && <table className="table table-add-form-results table-striped table-hover">
                      <thead>
                        <tr>
                          <th className="col-4 text-center">File Name</th>
                          <th className="col-4 text-center">File Type</th>
                          <th className="col-2 text-center">Upload Result</th>
                          <th className="col-2 text-center">View</th>
                        </tr>
                      </thead>
                      <tbody>
                        { results != null && results.files != null && results.files.map(doc => doc != null && doc.file && <tr key={ `${ doc.file.id }-${ doc.file.processedId ?? 0 }` }>
                          <td className="col-4"><div className="dz-filename">{ `${ doc.file.fileName }` }</div></td>
                          <td className="col-4">{ (doc.file.category === 0 || doc.file.category === 2) ? `${ doc.file.typeDescription }` : '' }</td>
                          <td className="col-2 upload-result">
                            { doc.success && <i className="icon icon-3x icon-check" /> }
                            { !doc.success && <i className="icon icon-3x icon-cross" /> }
                            { doc.error && <span style={ { paddingleft: '2rem' } }>{ doc.error.message }</span> }
                          </td>
                          <td className="col-2 text-center">
                            { /* Category 0 is Form */ }
                            { doc.success && doc.file.category === 0 && <button
                              className="btn btn-sm"
                              onClick={ () => this.onTakeForm(doc.file.processedId, resultAccount) }
                              type="button"
                              title="Open"
                            >
                              <span className="icon icon-apps" />
                            </button> }
                          </td>
                        </tr>)}
                      </tbody>
                    </table> }
                  </div> }
                </div>
              </div> }
            </div>
          </div> }
          <button
            type="button"
            id="dummy-add-form-btn"
            className="hide"
            onClick={ e => this.onFormSubmit(e) }
          >Add</button>
          { whsLinkAccounts && whsLinkAccounts.length > 0 && <div className="col-10 linking-container">
            <LinkWhsOperationMultiMonitor
              accounts={ whsLinkAccounts }
              onActiveMonitorsChange={ accounts => this.onActiveWhsLinkMonitorsChange(accounts) }
            />
          </div> }
        </form>
      </div>
    );
  }
}

const UploadPdfReduxForm = reduxForm({
  form: 'uploadingPdfForm',
})(AddFormsPopupForm);

const mapDispatchToProps = dispatch => ({ dispatch });

const mapStateToProps = state => ({
  initialValues: {
    clientId: state.settings && state.settings.clients && state.settings.clients.length === 1 ? state.settings.clients[0].id : '',
  },
  formTypes: state.auth && state.auth.settings ? state.auth.settings.formtypes : []
});

export const UploadPdfForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(UploadPdfReduxForm);

export default UploadPdfForm;
